import { useCallback, useEffect, useRef, useState } from 'react';
import { ArcReactor } from './components/ArcReactor';
import { ArmorSchematic } from './components/ArmorSchematic';
import { VideoModal } from './components/VideoModal';
import { armors, missions, timeline } from './data/archive';
import { useMotionPreference } from './hooks/useMotionPreference';

const nav = [
  { id: 'home', label: '首页' }, { id: 'armors', label: '装甲' },
  { id: 'missions', label: '任务' }, { id: 'core', label: '核心' },
];

function BrandMark() {
  return <a className="brand" href="#home"><span className="brand-sigil" aria-hidden="true"><i/><i/><i/></span><span>STARK<small>PRIVATE ARCHIVE</small></span></a>;
}

function LazyEarthVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || video.dataset.loaded) return;
      video.dataset.loaded = 'true';
      video.src = '/video/whole-earth-night.webm';
      video.load();
      video.play().catch(() => undefined);
      observer.disconnect();
    }, { rootMargin: '500px 0px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return <video ref={videoRef} muted loop playsInline preload="none" poster="/images/earth-night-poster.webp" />;
}

function App() {
  const { reduced, setReduced } = useMotionPreference();
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [helmetOpen, setHelmetOpen] = useState(false);
  const [armorIndex, setArmorIndex] = useState(2);
  const [armorPulse, setArmorPulse] = useState(0);
  const [selectedMission, setSelectedMission] = useState<(typeof missions)[number] | null>(null);
  const [timelineIndex, setTimelineIndex] = useState(0);
  const heroArt = useRef<HTMLDivElement>(null);
  const swipeStart = useRef<number | null>(null);
  const armor = armors[armorIndex];

  useEffect(() => {
    const sections = nav.map(({ id }) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveSection(visible.target.id);
    }, { rootMargin: '-25% 0px -55%', threshold: [0.05, .25, .6] });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const nodes = [...document.querySelectorAll<HTMLElement>('[data-reveal]')];
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { threshold: .12 });
    nodes.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const selectArmor = useCallback((next: number) => {
    const normalized = (next + armors.length) % armors.length;
    setArmorIndex(normalized); setArmorPulse(value => value + 1);
  }, []);

  const onArmorKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); selectArmor(armorIndex + 1); }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); selectArmor(armorIndex - 1); }
    if (event.key === 'Home') { event.preventDefault(); selectArmor(0); }
    if (event.key === 'End') { event.preventDefault(); selectArmor(armors.length - 1); }
  };

  const onHeroPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (reduced || event.pointerType === 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - .5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - .5) * 2;
    heroArt.current?.style.setProperty('--px', `${x * 8}px`);
    heroArt.current?.style.setProperty('--py', `${y * 6}px`);
  };

  const tilt = (event: React.PointerEvent<HTMLElement>) => {
    if (reduced || event.pointerType === 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--rx', `${((event.clientY - rect.top) / rect.height - .5) * -3}deg`);
    event.currentTarget.style.setProperty('--ry', `${((event.clientX - rect.left) / rect.width - .5) * 4}deg`);
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <BrandMark />
        <nav className={menuOpen ? 'is-open' : ''} aria-label="主要导航">
          {nav.map(({ id, label }, index) => <a key={id} href={`#${id}`} aria-current={activeSection === id ? 'page' : undefined} onClick={() => setMenuOpen(false)}><span>0{index + 1}</span>{label}</a>)}
        </nav>
        <div className="header-actions">
          <button className="motion-toggle" onClick={() => setReduced(!reduced)} aria-pressed={reduced} title="切换动效强度"><span className="motion-dot" />{reduced ? '静态' : '动效'}</button>
          <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="打开导航"><i/><i/></button>
        </div>
      </header>

      <main id="main">
        <section className="hero section" id="home" aria-labelledby="hero-title">
          <div className="hero-grid" />
          <div className="hero-copy" data-reveal>
            <div className="system-kicker"><span className="live-dot"/>STARK // PRIVATE ARCHIVE <b>ONLINE</b></div>
            <h1 id="hero-title"><span>钢铁</span><em>之心</em></h1>
            <p className="hero-subtitle">始于一个选择</p>
            <p className="hero-intro">装甲会不断升级，真正的核心始终是人的选择。进入一份关于设计、失败、责任与遗产的互动档案。</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#armors"><span>启动装甲库</span><i>↘</i></a>
              <a className="button button-secondary" href="#missions"><span>查看任务记录</span><i>→</i></a>
            </div>
            <div className="hero-metrics">
              <div><span>SYS.01</span><strong>42.8<small>GW</small></strong><p>能源输出</p></div>
              <div><span>SYS.02</span><strong>98.7<small>%</small></strong><p>系统完整性</p></div>
              <div><span>SYS.03</span><strong>07<small>UNITS</small></strong><p>装甲档案</p></div>
            </div>
          </div>

          <div className={`hero-visual ${helmetOpen ? 'helmet-open' : ''}`} onPointerMove={onHeroPointer} onPointerLeave={() => {
            heroArt.current?.style.setProperty('--px', '0px'); heroArt.current?.style.setProperty('--py', '0px');
          }}>
            <div className="hero-art" ref={heroArt}>
              <img src="/images/armor-engineer-hero.webp" alt="原创红金动力装甲工程原型，打开的头盔内可见冷青色机械结构" width="1792" height="1024" />
              <div className="faceplate" aria-hidden="true"><span className="faceplate-eye eye-left"/><span className="faceplate-eye eye-right"/><i/></div>
              <div className="target-reticle"><span/><span/><span/></div>
              <div className="hero-scan" />
            </div>
            <button className="helmet-control" onClick={() => setHelmetOpen(!helmetOpen)} aria-pressed={helmetOpen}>
              <span className="control-icon"><i/><i/></span>
              <span><small>HELMET CONTROL</small>{helmetOpen ? '关闭面甲' : '开启面甲'}</span>
            </button>
            <div className="jarvis-status"><span>J.A.R.V.I.S.</span><p><i/>系统在线 · 等待指令</p></div>
            <div className="visual-index">MK <strong>∞</strong></div>
          </div>
          <a href="#armors" className="scroll-cue"><span>SCROLL TO ACCESS</span><i/></a>
        </section>

        <section className="armors section" id="armors" aria-labelledby="armors-title">
          <div className="section-heading" data-reveal>
            <div><span className="eyebrow">ARMOR ARCHIVE // 01</span><h2 id="armors-title">每一次升级，<br/><em>都源于一次不足。</em></h2></div>
            <p>从洞穴中敲出的生存外壳，到近乎即时响应的纳米系统。型号记录技术进化，也记录设计者如何面对自己的错误。</p>
          </div>
          <div className="armor-console" data-reveal onTouchStart={e => swipeStart.current = e.touches[0].clientX} onTouchEnd={e => {
            if (swipeStart.current === null) return;
            const delta = e.changedTouches[0].clientX - swipeStart.current;
            if (Math.abs(delta) > 48) selectArmor(armorIndex + (delta < 0 ? 1 : -1));
            swipeStart.current = null;
          }}>
            <div className="armor-tabs" role="tablist" aria-label="选择装甲型号" onKeyDown={onArmorKeyDown}>
              {armors.map((item, index) => <button key={item.mark} id={`armor-tab-${index}`} role="tab" aria-selected={index === armorIndex} aria-controls="armor-panel" tabIndex={index === armorIndex ? 0 : -1} onClick={() => selectArmor(index)}>
                <span>{String(index + 1).padStart(2, '0')}</span><strong>{item.mark.replace('MARK ', 'MK ')}</strong><i/>
              </button>)}
            </div>
            <div className="armor-display" id="armor-panel" role="tabpanel" aria-labelledby={`armor-tab-${armorIndex}`} key={armorPulse}>
              <div className="armor-info">
                <div className="armor-meta"><span>{armor.phase}</span><b>{armor.year}</b></div>
                <h3>{armor.mark}<small>{armor.name}</small></h3>
                <p>{armor.description}</p>
                <ul>{armor.features.map(feature => <li key={feature}><i/> {feature}</li>)}</ul>
                <div className="armor-nav-buttons"><button onClick={() => selectArmor(armorIndex - 1)} aria-label="上一套装甲">←</button><span>{String(armorIndex + 1).padStart(2, '0')} / {String(armors.length).padStart(2, '0')}</span><button onClick={() => selectArmor(armorIndex + 1)} aria-label="下一套装甲">→</button></div>
              </div>
              <div className="armor-figure"><div className="figure-code">MODEL // {armor.mark.replace('MARK ', 'M')}</div><ArmorSchematic armor={armor}/><div className="scan-sweep"/></div>
              <div className="armor-stats">
                <div className="stats-note"><span>PERFORMANCE MATRIX</span><p>体验性可视化，非官方数值</p></div>
                {Object.entries(armor.stats).map(([key, value], index) => {
                  const labels: Record<string, string> = { mobility: '机动', defense: '防护', firepower: '火力', deployment: '部署' };
                  return <div className="stat" key={key}><div><span>0{index + 1} / {labels[key]}</span><b>{value}</b></div><div className="stat-track"><i style={{ '--value': `${value}%` } as React.CSSProperties}/><em style={{ left: `${value}%` }}/></div></div>;
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="missions section" id="missions" aria-labelledby="missions-title">
          <div className="earth-media" aria-hidden="true">
            <div className="earth-fallback"><i/><span/><b/></div>
            <LazyEarthVideo />
          </div>
          <div className="mission-overlay" />
          <div className="section-heading mission-heading" data-reveal>
            <div><span className="eyebrow">MISSION RECORDS // 02</span><h2 id="missions-title">装甲记录战斗，<br/><em>任务暴露选择。</em></h2></div>
            <p>以下内容基于 Marvel 官方角色与影片资料重新撰写。预告片只连接 Marvel Entertainment 官方发布，不下载或重新托管电影片段。</p>
          </div>
          <div className="mission-list">
            {missions.map(mission => <article key={mission.id} className="mission-card" data-reveal onPointerMove={tilt} onPointerLeave={e => { e.currentTarget.style.setProperty('--rx', '0deg'); e.currentTarget.style.setProperty('--ry', '0deg'); }}>
              <button className="mission-hit" onClick={() => setSelectedMission(mission)} aria-label={`播放${mission.title}官方预告片`} />
              <span className="mission-index">{mission.index}</span>
              <div className="mission-year"><span>{mission.year}</span><i/></div>
              <div className="mission-copy"><p>PHASE {mission.index}</p><h3>{mission.title}<small>{mission.subtitle}</small></h3><div>{mission.copy}</div></div>
              <div className="play-control"><span>PLAY</span><i>▶</i></div>
            </article>)}
          </div>
          <p className="nasa-credit">BACKGROUND VISUAL · NASA GODDARD SCIENTIFIC VISUALIZATION STUDIO / EARTH OBSERVATORY · NO ENDORSEMENT IMPLIED</p>
        </section>

        <section className="core section" id="core" aria-labelledby="core-title">
          <div className="core-grid" />
          <div className="section-heading core-heading" data-reveal>
            <div><span className="eyebrow">ARC CORE // 03</span><h2 id="core-title">驱动装甲的不是能源，<br/><em>是选择的方向。</em></h2></div>
            <p>四个阶段构成一条并不完美的进化曲线。它从“活下来”开始，以“让别人继续前进”结束。</p>
          </div>
          <div className="core-experience" data-reveal>
            <div className="timeline-side timeline-left">
              {timeline.slice(0, 2).map((item, index) => <button key={item.key} className={timelineIndex === index ? 'active' : ''} onClick={() => setTimelineIndex(index)}><span>{item.value}</span><strong>{item.key}<small>{item.cn}</small></strong><i/></button>)}
            </div>
            <div className="reactor-stage" onPointerEnter={() => document.documentElement.classList.add('reactor-boost')} onPointerLeave={() => document.documentElement.classList.remove('reactor-boost')}>
              <div className="reactor-data data-a"><span>CORE TEMP</span><b>2187 K</b></div>
              <div className="reactor-data data-b"><span>STABILITY</span><b>98.7%</b></div>
              <ArcReactor />
              <div className="timeline-detail" aria-live="polite"><span>{timeline[timelineIndex].key} // {timeline[timelineIndex].value}</span><strong>{timeline[timelineIndex].cn}</strong><p>{timeline[timelineIndex].note}</p></div>
            </div>
            <div className="timeline-side timeline-right">
              {timeline.slice(2).map((item, index) => <button key={item.key} className={timelineIndex === index + 2 ? 'active' : ''} onClick={() => setTimelineIndex(index + 2)}><i/><strong>{item.key}<small>{item.cn}</small></strong><span>{item.value}</span></button>)}
            </div>
          </div>
          <blockquote data-reveal><span>“</span><p>装甲可以解决眼前的问题。<br/>但它无法替你决定，<em>要成为怎样的人。</em></p><small>THE HUMAN VARIABLE // ARCHIVE CONCLUSION</small></blockquote>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-top"><BrandMark/><p>一份关于技术、责任与选择的非官方互动档案。</p><a href="#home">返回顶部 ↑</a></div>
        <div className="footer-legal"><p>本项目为非官方粉丝概念网站。Iron Man、Marvel 及相关角色属于其各自权利人。本项目不代表 Marvel、Disney、NASA 或任何素材作者的认可、赞助或背书。</p><div><a href="/SOURCES.md" target="_blank">素材与资料来源 ↗</a><span>© 2026 CONCEPT STUDY</span></div></div>
      </footer>
      {selectedMission && <VideoModal mission={selectedMission} onClose={() => setSelectedMission(null)} />}
    </div>
  );
}

export default App;
